import React, { useContext, useEffect } from 'react';
import useRootSelector from 'common/hooks/useRootSelector';
import { useForm, FormProvider } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Box } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import isNumber from 'lodash/isNumber';
import { fetchPosKioskRegisterTotals } from 'modules/pos-kiosk/state/register/actions';
import * as selectors from 'modules/pos-kiosk/state/register/selectors';
import { PeakModules } from 'common/constants/peakModules';
import { StepContext } from 'common/createContext/stepContext';
import {
  IPosKioskReconcileDTO,
  IPosKioskRegisterTotalsImt,
} from 'modules/pos-kiosk/interfaces/registers';
import { PaymentsType } from 'common/interfaces/invoices';
import { LoadingBackdrop } from 'common/components';
import TotalSalesItem from './TotalSalesItem';
import { useAppDispatch } from 'store/hooks';
import { getRequiredErrorMessage } from 'common/utils/validation';

import messages from 'modules/pos-kiosk/messages/messages';
import inputErrors from 'common/messages/inputErrors';

interface IProps {
  actual: Partial<IPosKioskReconcileDTO>;
  module: PeakModules;
  registerId: string;
}

const getValidationSchema = (cashVariance, checkVariance) => {
  return yup.object().shape({
    cashVarianceNote: cashVariance
      ? yup
          .string()
          .trim()
          .max(1000, () => inputErrors.varianceMaxLengthError)
          .required(getRequiredErrorMessage)
      : yup.string(),
    checkVarianceNote: checkVariance
      ? yup
          .string()
          .trim()
          .max(1000, () => inputErrors.varianceMaxLengthError)
          .required(getRequiredErrorMessage)
      : yup.string(),
  });
};

const ReconcileTotalsStep = ({ actual, module, registerId }: IProps): JSX.Element => {
  const dispatch = useAppDispatch();

  const registerTotals: IPosKioskRegisterTotalsImt = useRootSelector(
    selectors.selectPosKioskRegisterTotals,
  );
  const isRegisterTotalsLoading: boolean = useRootSelector(
    selectors.selectPosKioskRegisterTotalsLoading,
  );

  const { onBack, renderFooter, onNext } = useContext(StepContext);

  const { cashAmount, checkAmount } = actual?.cashDesk || {};

  useEffect(() => {
    if (isNumber(cashAmount) && isNumber(checkAmount)) {
      dispatch(fetchPosKioskRegisterTotals(registerId, cashAmount, checkAmount, module));
    }
  }, [cashAmount, checkAmount, dispatch, module, registerId]);

  const cashVariance = registerTotals
    .get('totalSalesItemList')
    ?.find(payItem => payItem.get('paymentMethodTitle') === PaymentsType.CASH)
    ?.get('variance');

  const checkVariance = registerTotals
    .get('totalSalesItemList')
    ?.find(payItem => payItem.get('paymentMethodTitle') === PaymentsType.CHECKING_SAVINGS)
    ?.get('variance');

  const formMethods = useForm<IPosKioskReconcileDTO>({
    resolver: yupResolver(getValidationSchema(cashVariance, checkVariance)) as any, // TODO - PRM-1810 need resolver type
    mode: 'onBlur',
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = formMethods;

  const onSubmit = data => {
    if (onNext) {
      onNext(data);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form>
        {registerTotals.get('totalSalesItemList')?.map(totalSalesItem => (
          <TotalSalesItem item={totalSalesItem} />
        ))}

        {!isValid && (
          <Box mt={2}>
            <Alert severity="warning" icon={false}>
              <FormattedMessage {...messages.enterVarianceText} />
            </Alert>
          </Box>
        )}

        <LoadingBackdrop isLoading={isRegisterTotalsLoading} />

        {renderFooter ? renderFooter(onBack, handleSubmit(onSubmit)) : null}
      </form>
    </FormProvider>
  );
};

export default ReconcileTotalsStep;
